import React, { useState, useEffect } from 'react'
import axios from 'axios'
import './Slots.css'
import Header from '../components/Header'
import { useNavigate } from 'react-router-dom'
import { toast, ToastContainer } from 'react-toastify'

const getAuthToken = () =>
  document.cookie.replace(/(?:(?:^|.*;\s*)auth\s*=\s*([^;]*).*$)|^.*$/, "$1")

const hourlySlots = Array.from({ length: 24 }, (_, i) => `${i.toString().padStart(2, '0')}:00`)

const SlotPage = () => {
  const [slotList, setSlotList] = useState([])
  const [editingSlot, setEditingSlot] = useState(null)
  const [newPrice, setNewPrice] = useState('')
  const [newSlot, setNewSlot] = useState({ startTime: '', capacity: '', price: '', timePeriod: '1' })
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => { fetchSlots() }, [])

  const fetchSlots = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/slots`, {
        headers: { auth: getAuthToken() },
      })
      const sorted = (response.data || []).sort((a, b) =>
        new Date(`1970-01-01T${a.startTime}`) - new Date(`1970-01-01T${b.startTime}`)
      )
      setSlotList(sorted)
    } catch (error) {
      console.error('Error fetching slots:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleAddSlot = async () => {
    if (!newSlot.startTime || !newSlot.capacity || !newSlot.price) {
      toast.error('Please fill all fields')
      return
    }
    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/slots`, newSlot, {
        headers: { auth: getAuthToken() },
      })
      setNewSlot({ startTime: '', capacity: '', price: '', timePeriod: '1' })
      fetchSlots()
      toast.success('Slot added successfully')
    } catch (error) {
      toast.error('Error adding slot')
    }
  }

  const handleSaveSlot = async (id) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/slots/${id}`,
        { price: newPrice },
        { headers: { auth: getAuthToken() } }
      )
      setEditingSlot(null)
      setNewPrice('')
      fetchSlots()
      toast.success('Price updated')
    } catch {
      toast.error('Error updating price')
    }
  }

  const handleToggleSlot = async (slot) => {
    const newVal = slot.timePeriod === 1 ? 0 : 1
    try {
      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/slots/disable/${slot.id}`,
        { enable: newVal },
        { headers: { auth: getAuthToken() } }
      )
      fetchSlots()
    } catch {
      toast.error('Error toggling slot')
    }
  }

  const handleDeleteSlot = async (id) => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/slots/${id}`, {
        headers: { auth: getAuthToken() },
      })
      fetchSlots()
      toast.success('Slot deleted')
    } catch {
      toast.error('Error deleting slot')
    }
  }

  const enabledisableAllSlot = async (val) => {
    for (const slot of slotList) {
      try {
        await axios.put(
          `${process.env.REACT_APP_API_BASE_URL}/api/slots/disable/${slot.id}`,
          { enable: val },
          { headers: { auth: getAuthToken() } }
        )
      } catch {
        toast.error(`Failed for slot ${slot.startTime}`)
      }
    }
    fetchSlots()
    toast.success(val === 1 ? 'All slots enabled' : 'All slots disabled')
  }

  const enabledCount = slotList.filter(s => s.timePeriod === 1).length

  return (
    <div className="slots-page">
      <Header />
      <ToastContainer position="top-right" autoClose={2500} theme="dark" />
      <div className="sl-content">
        <div className="sl-page-header">
          <div>
            <h1 className="sl-page-title">Slot Management</h1>
            <p className="sl-page-sub">{enabledCount} of {slotList.length} slots active</p>
          </div>
          <div className="sl-header-actions">
            <button className="sl-btn-ghost" onClick={() => navigate('/equipment')}>← Equipment</button>
            <button className="sl-btn-ghost" onClick={() => navigate('/gallery')}>Gallery →</button>
          </div>
        </div>

        <div className="sl-add-card">
          <h2 className="sl-card-title">Add New Slot</h2>
          <div className="sl-add-form">
            <div className="sl-form-group">
              <label>Start Time</label>
              <select value={newSlot.startTime} onChange={e => setNewSlot({ ...newSlot, startTime: e.target.value })}>
                <option value="" disabled>Select time</option>
                {hourlySlots.map(h => <option key={h} value={h}>{h}</option>)}
              </select>
            </div>
            <div className="sl-form-group">
              <label>Capacity</label>
              <input
                type="number"
                placeholder="Max members"
                value={newSlot.capacity}
                onChange={e => setNewSlot({ ...newSlot, capacity: e.target.value })}
              />
            </div>
            <div className="sl-form-group">
              <label>Price (₹)</label>
              <input
                type="number"
                placeholder="Per session"
                value={newSlot.price}
                onChange={e => setNewSlot({ ...newSlot, price: e.target.value })}
              />
            </div>
            <button className="sl-btn-add" onClick={handleAddSlot}>+ Add Slot</button>
          </div>
        </div>

        <div className="sl-list-card">
          <div className="sl-list-header">
            <h2 className="sl-card-title">All Slots</h2>
            <div className="sl-bulk-actions">
              <button className="sl-btn-danger-ghost" onClick={() => enabledisableAllSlot(0)}>Disable All</button>
              <button className="sl-btn-success-ghost" onClick={() => enabledisableAllSlot(1)}>Enable All</button>
            </div>
          </div>

          {loading ? (
            <div className="sl-empty"><div className="sl-spinner" /><p>Loading slots...</p></div>
          ) : slotList.length === 0 ? (
            <div className="sl-empty">
              <div className="sl-empty-icon">🕐</div>
              <p>No slots yet. Add your first slot above.</p>
            </div>
          ) : (
            <div className="sl-table-wrap">
              <table className="sl-table">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Capacity</th>
                    <th>Price</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {slotList.map(slot => (
                    <tr key={slot.id}>
                      <td className="sl-time">{slot.startTime}</td>
                      <td>{slot.capacity} members</td>
                      <td>
                        {editingSlot === slot.id ? (
                          <input
                            type="number"
                            value={newPrice}
                            onChange={e => setNewPrice(e.target.value)}
                            className="sl-price-input"
                          />
                        ) : (
                          <span className="sl-price">₹{slot.price}</span>
                        )}
                      </td>
                      <td>
                        <button
                          className={`sl-toggle ${slot.timePeriod === 1 ? 'enabled' : 'disabled'}`}
                          onClick={() => handleToggleSlot(slot)}
                        >
                          {slot.timePeriod === 1 ? '● Active' : '○ Inactive'}
                        </button>
                      </td>
                      <td>
                        <div className="sl-actions">
                          {editingSlot === slot.id ? (
                            <>
                              <button className="sl-btn-save" onClick={() => handleSaveSlot(slot.id)}>Save</button>
                              <button className="sl-btn-cancel" onClick={() => { setEditingSlot(null); setNewPrice('') }}>Cancel</button>
                            </>
                          ) : (
                            <>
                              <button className="sl-btn-edit" onClick={() => { setEditingSlot(slot.id); setNewPrice(slot.price) }}>Edit Price</button>
                              <button className="sl-btn-delete" onClick={() => handleDeleteSlot(slot.id)}>Delete</button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default SlotPage
